/* ============================================================
 * <mobile-menu-v2> — James Oro mobile hamburger menu
 *
 * Responsibilities:
 *  - In-place accordion expand/collapse with max-height animation
 *    (no <details> — we control timing for smooth open/close).
 *  - Hamburger 3-bar → X icon state, driven by the outer Dawn
 *    <details id="Details-menu-drawer-container"> open attribute
 *    via MutationObserver. Reacts to ESC, focusout, tap-outside,
 *    not just direct clicks.
 *  - Honors prefers-reduced-motion (snap, no animation).
 * ============================================================ */

class MobileMenuV2 extends HTMLElement {
  connectedCallback() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.bindAccordions();
    this.bindSearchReset();
    this.observeDrawer();
  }

  disconnectedCallback() {
    if (this.drawerObserver) this.drawerObserver.disconnect();
  }

  /* ---- Accordions ---- */
  bindAccordions() {
    // Dawn's MenuDrawer.bindEvents() (assets/global.js:337) attaches an
    // onCloseButtonClick handler to every <button> inside the drawer
    // (except .localization-selector). That handler runs closeSubmenu()
    // on the closest <details>, which is our outer drawer — so any tap
    // on an accordion trigger would close the whole drawer.
    //
    // Clone-replace each trigger to strip ALL listeners (including
    // Dawn's), then bind only ours. This works regardless of upgrade
    // order between HeaderDrawer and MobileMenuV2.
    const triggers = Array.from(this.querySelectorAll('[data-jo-accordion-trigger]'));
    triggers.forEach((trigger) => {
      const clone = trigger.cloneNode(true);
      trigger.parentNode.replaceChild(clone, trigger);
      clone.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.toggleAccordion(clone);
      });
    });
  }

  /* ---- Search reset button ----
   * Same Dawn-MenuDrawer-binds-every-button problem as accordions: the
   * <button type="reset"> inside the search form was inheriting Dawn's
   * close handler, so clicking the X to clear a query was closing the
   * whole drawer instead. Clone to strip Dawn's listener; native
   * type="reset" semantics survive cloning, so the form still clears.
   */
  bindSearchReset() {
    const reset = this.querySelector('.jo-mnav__search button.reset__button');
    if (!reset) return;
    const clone = reset.cloneNode(true);
    reset.parentNode.replaceChild(clone, reset);
    clone.addEventListener('click', (event) => {
      // Stop Dawn (and anything else upstream) — the form's native reset
      // behavior is a default action and happens regardless of listeners.
      event.stopImmediatePropagation();
      // Fire an input event so <predictive-search> notices the value
      // cleared and hides its results dropdown.
      const input = this.querySelector('.jo-mnav__search input[type="search"]');
      if (input) {
        // Defer to next tick: the native form reset runs as default action
        // after click handlers, so input.value is still the old text here.
        setTimeout(() => input.dispatchEvent(new Event('input', { bubbles: true })), 0);
      }
    });
  }

  toggleAccordion(trigger) {
    const panelId = trigger.getAttribute('aria-controls');
    if (!panelId) return;
    const panel = this.querySelector('#' + CSS.escape(panelId));
    if (!panel) return;

    const isOpen = trigger.getAttribute('aria-expanded') === 'true';
    if (isOpen) {
      this.closePanel(trigger, panel);
    } else {
      this.openPanel(trigger, panel);
    }
  }

  openPanel(trigger, panel) {
    trigger.setAttribute('aria-expanded', 'true');
    panel.classList.add('is-open');

    if (this.reducedMotion) {
      panel.style.maxHeight = 'none';
      return;
    }

    panel.style.willChange = 'max-height';
    // Start from current (0) → animate to scrollHeight, then clear cap so
    // late content (images, async stuff) doesn't get clipped.
    const target = panel.scrollHeight;
    panel.style.maxHeight = target + 'px';

    const onEnd = (e) => {
      if (e.propertyName !== 'max-height') return;
      panel.style.maxHeight = 'none';
      panel.style.willChange = '';
      panel.removeEventListener('transitionend', onEnd);
    };
    panel.addEventListener('transitionend', onEnd);
  }

  closePanel(trigger, panel) {
    trigger.setAttribute('aria-expanded', 'false');

    if (this.reducedMotion) {
      panel.classList.remove('is-open');
      panel.style.maxHeight = '';
      return;
    }

    panel.style.willChange = 'max-height';
    // If maxHeight is currently "none" (post-open), snap back to a numeric
    // value before transitioning so the browser has a starting point.
    const current = panel.scrollHeight;
    panel.style.maxHeight = current + 'px';
    // Force reflow so the next assignment animates.
    // eslint-disable-next-line no-unused-expressions
    panel.offsetHeight;
    panel.style.maxHeight = '0px';

    const onEnd = (e) => {
      if (e.propertyName !== 'max-height') return;
      panel.classList.remove('is-open');
      panel.style.maxHeight = '';
      panel.style.willChange = '';
      panel.removeEventListener('transitionend', onEnd);
    };
    panel.addEventListener('transitionend', onEnd);
  }

  /* ---- Hamburger icon state ---- */
  observeDrawer() {
    const drawer = document.getElementById('Details-menu-drawer-container');
    if (!drawer) return;
    const summary = drawer.querySelector('summary.header__icon--menu');
    if (!summary) return;

    const sync = () => {
      if (drawer.hasAttribute('open')) {
        summary.classList.add('is-open');
      } else {
        summary.classList.remove('is-open');
        // collapse any open accordions so reopening is fresh
        this.querySelectorAll('[data-jo-accordion-trigger][aria-expanded="true"]').forEach((t) => {
          const panel = this.querySelector('#' + CSS.escape(t.getAttribute('aria-controls')));
          t.setAttribute('aria-expanded', 'false');
          if (panel) {
            panel.classList.remove('is-open');
            panel.style.maxHeight = '';
          }
        });
      }
    };

    this.drawerObserver = new MutationObserver(sync);
    this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['open'] });
    sync();
  }
}

if (!customElements.get('mobile-menu-v2')) {
  customElements.define('mobile-menu-v2', MobileMenuV2);
}
